import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { inventoryItemsApi, storageFacilitiesApi } from '../inventoryApi';
import type {
  InventoryItemOption,
  OwnershipType,
  StorageFacilityOption,
} from '../inventoryTypes';
import { OWNERSHIP, OWNERSHIP_LABEL } from '../inventoryTypes';
import { stockUnitsApi } from '../stockApi';
import type { StockUnitDetail, StockUnitStatus } from '../stockTypes';
import { UNIT_HELD_STATUSES, UNIT_STATUS, UNIT_STATUS_LABEL } from '../stockTypes';
import { sampleStockUnit } from '../utils/sampleStock';

function toNumberOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : Number(trimmed);
}

/**
 * Receive one serialized thing, or correct one.
 *
 * **The status here moves only between states that still HOLD the unit** — in stock, damaged,
 * under repair. Taking one off the owned tally is the write-off, under its own key, and a form
 * that could express it here would be the second route around that key. The server refuses it
 * too; this is the form not offering what it knows would be refused.
 *
 * **A unit MOVES between facilities and a lot does not**, which is the one asymmetry with the
 * batch form: the facility picker stays on the edit here, where the batch form draws it only on
 * a receipt.
 */
export function StockUnitModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null receives a new unit. */
  editing: StockUnitDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = editing !== null;

  const [itemId, setItemId] = useState(editing?.inventoryItemId ?? '');
  const [facilityId, setFacilityId] = useState(editing?.storageFacilityId ?? '');
  const [serialNumber, setSerialNumber] = useState(editing?.serialNumber ?? '');
  const [ownership, setOwnership] = useState(
    editing?.ownership === null || editing?.ownership === undefined ? '' : String(editing.ownership),
  );
  const [acquisitionDate, setAcquisitionDate] = useState(editing?.acquisitionDate ?? '');
  const [fund, setFund] = useState(editing?.fund ?? '');
  const [leaseEndDate, setLeaseEndDate] = useState(editing?.leaseEndDate ?? '');
  const [leasedFrom, setLeasedFrom] = useState(editing?.leasedFrom ?? '');
  const [termEndDate, setTermEndDate] = useState(editing?.termEndDate ?? '');
  const [unitCost, setUnitCost] = useState(
    editing?.unitCost === null || editing?.unitCost === undefined ? '' : String(editing.unitCost),
  );
  const [status, setStatus] = useState(String(editing?.status ?? UNIT_STATUS.inStock));
  const [statusReason, setStatusReason] = useState(editing?.statusReason ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');

  const [items, setItems] = useState<InventoryItemOption[]>([]);
  const [facilities, setFacilities] = useState<StorageFacilityOption[]>([]);
  // TRUE on the first render: the fetch starts in an effect, which runs AFTER paint.
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoadingCatalogs(true);

    // The facility picker is on BOTH forms here, so both fetch. The item feed is only needed
    // on a create, but one Promise.all is cheaper to read than a branch for one list.
    Promise.all([
      inventoryItemsApi.options(controller.signal),
      storageFacilitiesApi.options(controller.signal),
    ])
      .then(([loadedItems, loadedFacilities]) => {
        setItems(loadedItems);
        setFacilities(loadedFacilities);
      })
      // A failed picker load names no field, so it must not go through the save path's error
      // state — the fields render empty rather than the form reporting a rejection.
      .catch(() => undefined)
      .finally(() => setLoadingCatalogs(false));

    return () => controller.abort();
  }, [open]);

  /*
    Only items whose stock is NAMED one row at a time. A quantity item is counted in lots, and
    offering one here would produce a rejection the reader could not have seen coming.
  */
  const offeredItems = items.filter((i) => i.tracksSerial);

  const owned = ownership === String(OWNERSHIP.owned);
  const outOfService = Number(status) !== UNIT_STATUS.inStock;

  const valid =
    serialNumber.trim().length > 0 &&
    facilityId !== '' &&
    (isEdit || itemId !== '') &&
    (!outOfService || statusReason.trim().length > 0);

  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    // Cleared where the tenure says it cannot apply, matching what the server does on write: a
    // unit re-pointed from leased to owned must not keep a lease end date behind the reader.
    const lease = owned ? null : leaseEndDate || null;
    const lessor = owned ? null : leasedFrom.trim() || null;

    try {
      if (editing) {
        await stockUnitsApi.update(editing.id, {
          serialNumber: serialNumber.trim(),
          storageFacilityId: facilityId,
          ownership: ownership === '' ? null : (Number(ownership) as OwnershipType),
          acquisitionDate: acquisitionDate || null,
          fund: fund.trim() || null,
          leaseEndDate: lease,
          leasedFrom: lessor,
          termEndDate: termEndDate || null,
          unitCost: toNumberOrNull(unitCost),
          status: Number(status) as StockUnitStatus,
          statusReason: outOfService ? statusReason.trim() || null : null,
          notes: notes.trim() || null,
        });
      } else {
        await stockUnitsApi.create({
          inventoryItemId: itemId,
          storageFacilityId: facilityId,
          serialNumber: serialNumber.trim(),
          ownership: ownership === '' ? null : (Number(ownership) as OwnershipType),
          acquisitionDate: acquisitionDate || null,
          fund: fund.trim() || null,
          leaseEndDate: lease,
          leasedFrom: lessor,
          termEndDate: termEndDate || null,
          unitCost: toNumberOrNull(unitCost),
          notes: notes.trim() || null,
        });
      }

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the unit.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'stock-unit' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit unit' : 'Receive a unit'}
      size="lg"
      id="stock-unit-modal"
      loading={loadingCatalogs}
      autogenerate={
        isEdit
          ? undefined
          : {
              onGenerate: () => {
                const sample = sampleStockUnit({ items, facilities });

                setItemId(sample.inventoryItemId);
                setFacilityId(sample.storageFacilityId);
                setSerialNumber(sample.serialNumber);
                setAcquisitionDate(sample.acquisitionDate);
                setFund(sample.fund);
                setUnitCost(sample.unitCost);
                setNotes(sample.notes);

                /*
                  OWNERSHIP is left alone. It reveals the lease end date, so choosing one would
                  make the same button produce a different form on each click — and the server
                  falls back to the catalog item's own declaration when it is empty, which is
                  the ordinary path rather than a gap.
                */
              },
              disabled: saving || loadingCatalogs,
            }
      }
      confirm={{
        id: 'stock-unit-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="stock-unit-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="stock-unit-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {isEdit ? (
          // What the unit IS. Re-pointing it at another item would rewrite what the thing is,
          // so that is a fact here rather than a question.
          <PreviewTable
            variant="table-display"
            rows={[{ label: 'Item', value: `${editing.sku} — ${editing.itemName}` }]}
          />
        ) : (
          <Select
            id="stock-unit-inventory-item-id"
            label="Item"
            required
            description="What this unit is. Only items tracked by serial are offered — anything counted as a quantity is recorded as a batch instead."
            placeholder="Choose an item"
            value={itemId}
            onChange={setItemId}
            options={offeredItems.map((i) => ({
              value: i.id,
              label: i.name,
              sublabel: i.sku,
              keywords: i.sku,
            }))}
            remote
            loading={loadingCatalogs}
            disabled={saving}
            error={error?.fieldError('inventoryItemId')}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="stock-unit-serial-number"
            label="Serial number"
            required
            description="The manufacturer's serial. Held once per item — two different products may carry the same serial, so it is not unique across the table."
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            maxLength={100}
            disabled={saving}
            error={error?.fieldError('serialNumber')}
          />

          <Select
            id="stock-unit-storage-facility-id"
            label="Facility"
            required
            description="Where it currently is. Unlike a lot, a unit moves — correcting this here is how it is recorded."
            placeholder="Choose a facility"
            value={facilityId}
            onChange={setFacilityId}
            options={facilities.map((f) => ({ value: f.id, label: f.name, sublabel: f.code }))}
            remote
            loading={loadingCatalogs}
            disabled={saving}
            error={error?.fieldError('storageFacilityId')}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="stock-unit-ownership"
            label="Held as"
            description="Owned outright or rented. Leave empty to fall back to the catalog item's own declaration — two vans of one model can be held differently, which is why this sits on the unit."
            placeholder="Fall back to the item"
            value={ownership}
            onChange={setOwnership}
            sort="none"
            disabled={saving}
            options={Object.entries(OWNERSHIP_LABEL).map(([value, label]) => ({ value, label }))}
            error={error?.fieldError('ownership')}
          />

          <TextField
            id="stock-unit-unit-cost"
            label="Unit cost"
            type="number"
            min={0}
            step="0.01"
            description="What this one cost. A snapshot, like a lot's — the catalog's price is the current reference figure, which is a different fact."
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
            disabled={saving}
            error={error?.fieldError('unitCost')}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="stock-unit-acquisition-date"
            label="Acquired"
            description="When this unit was obtained."
            value={acquisitionDate}
            onChange={setAcquisitionDate}
            disabled={saving}
            error={error?.fieldError('acquisitionDate')}
          />

          {/*
            WHAT IT WAS BOUGHT UNDER, beside WHEN it was bought — one purchase, two facts.

            Free text rather than a picker, because nothing aggregates by it: an ICS or a PAR
            prints it in a column and a person reads it. The day a report groups by fund this
            is a catalog, not a wider column.
          */}
          <TextField
            id="stock-unit-fund"
            label="Fund"
            description="The funding source it was bought under — printed on the ICS or PAR."
            value={fund}
            onChange={(e) => setFund(e.target.value)}
            maxLength={120}
            disabled={saving}
            error={error?.fieldError('fund')}
          />

          {/*
            Drawn unless the tenure says OWNED. Left as "fall back to the item" it stays
            available, because the catalog may well declare the item leased and the form cannot
            see that — refusing it here would reject a legitimate lease.
          */}
          {owned ? null : (
            <DateField
              id="stock-unit-lease-end-date"
              label="Lease ends"
              description="When the lease on THIS unit ends. Three rented vans can come back on three different days, which is why the date is here rather than on the item."
              value={leaseEndDate}
              onChange={setLeaseEndDate}
              disabled={saving}
              error={error?.fieldError('leaseEndDate')}
            />
          )}

          {/*
            WHO IT CAME FROM, and this form has to draw it or an ordinary edit would DESTROY
            it. The registration collects a lessor per row; the write applies what it is given,
            so a form that could not see the value would send null and wipe it on every save —
            the government-ID rule, reaching a third module.

            Free text, and the cost is stated where the column is: two spellings of one lessor
            are two lessors no report can group. Nothing groups by it today.
          */}
          {owned ? null : (
            <TextField
              id="stock-unit-leased-from"
              label="Leased from"
              description="Who this unit is leased from — the hire company or landlord. Free text, so the same lessor typed two ways is two lessors."
              value={leasedFrom}
              onChange={(e) => setLeasedFrom(e.target.value)}
              disabled={saving}
              error={error?.fieldError('leasedFrom')}
            />
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="stock-unit-term-end-date"
            label="Term ends"
            description="When this unit's licence or service term lapses. Subscriptions are tracked per seat precisely because every seat expires on its own date."
            value={termEndDate}
            onChange={setTermEndDate}
            disabled={saving}
            error={error?.fieldError('termEndDate')}
          />
        </div>

        {isEdit ? (
          <>
            <Select
              id="stock-unit-status"
              label="Condition"
              required
              description="Only a state that still HOLDS the unit. Disposed and Stolen take it off the owned tally, which is Write off — a heavier decision with its own permission."
              value={status}
              onChange={setStatus}
              sort="none"
              clearable={false}
              disabled={saving}
              options={UNIT_HELD_STATUSES.map((s) => ({
                value: String(s),
                label: UNIT_STATUS_LABEL[s],
              }))}
              error={error?.fieldError('status')}
            />

            {outOfService ? (
              <TextField
                id="stock-unit-status-reason"
                label="Why is it out of service?"
                required
                description="Cleared automatically when the unit returns to stock, so a working unit cannot keep a fault description behind the reader's back."
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                maxLength={500}
                disabled={saving}
                error={error?.fieldError('statusReason')}
              />
            ) : null}
          </>
        ) : null}

        <TextField
          id="stock-unit-notes"
          label="Notes"
          description="Anything worth knowing about this particular unit."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('notes')}
        />
      </div>
    </Modal>
  );
}
